/**
 * The panel's standing order: one line telling the operator what to do next.
 *
 * A pure function of state, so it can never disagree with the simulation. The
 * first matching rule wins, and the list is ordered by what a new player needs
 * to learn first. Once every teaching rule has been satisfied it falls through
 * to a running status, so the strip is never empty and never nags.
 */

import { GM_MIN_THOUGHTS, LEVER_UNLOCK_COUNT } from '../data/balance.ts';
import { SKILLS } from '../data/skills.ts';
import {
  greyMatterGain,
  voidHeat,
  voidShare,
  secondsToMeltdown,
  thoughtsToFirstGM,
  type Modifiers,
} from './formulas.ts';
import type { GameState } from './state.ts';
import Decimal from 'break_infinity.js';

export interface Directive {
  /** Stamped label, upper-cased by the panel. */
  label: string;
  /** One or two sentences. Institutional, declarative. */
  body: string;
  /** Set while the rule is teaching something new, so the strip can highlight. */
  teaching: boolean;
}

const CHEAPEST_SKILL = Math.min(...SKILLS.map((s) => s.cost));

export function currentDirective(state: GameState, mods: Modifiers): Directive {
  const neurons = state.generators[0] ?? 0;

  // --- Learning the loop ---------------------------------------------------

  if (neurons === 0) {
    return {
      label: 'Standing order',
      body:
        'The jar sparks on its own, slowly. Spend those sparks on a Neuron: ' +
        'it fires for you, permanently.',
      teaching: true,
    };
  }

  if (state.unlocks.lever !== true) {
    const left = Math.max(0, LEVER_UNLOCK_COUNT - neurons);
    return {
      label: 'Standing order',
      body:
        `Install ${left} more Neuron${left === 1 ? '' : 's'}. ` +
        'At ten the mechanical stop comes off the clock lever.',
      teaching: true,
    };
  }

  if (state.unlocks.overclocked !== true) {
    return {
      label: 'Standing order',
      body:
        'The lever is free. Above 100% output climbs faster than the rig can ' +
        'shed heat — that is the whole trade. Push it and watch the core temp.',
      teaching: true,
    };
  }

  if (state.unlocks.vented !== true && state.heat >= 45) {
    return {
      label: 'Caution',
      body:
        'Core temp is climbing and will not stop on its own above 100%. ' +
        'Vent Coolant dumps heat instantly, then needs time to refill.',
      teaching: true,
    };
  }

  if (state.unlocks.voidExplained !== true && voidShare(state, mods) > 0.25) {
    return {
      label: 'Caution',
      body:
        'Intrusive thoughts are crowding in. The longer a run goes on the more ' +
        'of them there are, and holding them back costs heat. No run lasts ' +
        'forever — cash out before they take the room.',
      teaching: true,
    };
  }

  if (state.stats.meltdowns === 0) {
    const gain = greyMatterGain(state, mods);
    if (gain.gt(0)) {
      return {
        label: 'Ready',
        body:
          `This run is worth ${gain.toString()} idea${gain.eq(1) ? '' : 's'}. Forcing it melts ` +
          'the brain and destroys the run — sparks and generators both. ' +
          'The idea is kept forever, and grows the longer you hold.',
        teaching: true,
      };
    }
    if (state.thoughtsThisRun.gte(new Decimal(GM_MIN_THOUGHTS).div(10))) {
      return {
        label: 'Standing order',
        body:
          'Keep earning. Once the run has produced enough sparks the brain can be ' +
          'forced to give up an idea, which is never lost.',
        teaching: true,
      };
    }
  }

  if (state.stats.meltdowns > 0 && Object.keys(state.skills).length === 0) {
    if (state.greyMatter.gte(CHEAPEST_SKILL)) {
      return {
        label: 'Standing order',
        body:
          'Ideas survive every meltdown. Spend them on Memories — that is the only ' +
          'thing that makes the next run faster than the last.',
        teaching: true,
      };
    }
  }

  // --- Running status ------------------------------------------------------

  if (voidHeat(state, mods) > 0.5) {
    return {
      label: 'Caution',
      body:
        `The void is adding ${voidHeat(state, mods).toFixed(1)} heat a second and ` +
        'climbing. This run is on borrowed time whatever the clock says.',
      teaching: false,
    };
  }

  if (state.heat >= 97) {
    return {
      label: 'Critical',
      body: 'Meltdown imminent. Vent now, or let it go and take the idea.',
      teaching: false,
    };
  }

  const eta = secondsToMeltdown(state, mods);
  if (eta === null) {
    return {
      label: 'Nominal',
      body:
        state.clock <= 100
          ? 'At or below 100% the rig cools as fast as it heats. It will hold here forever.'
          : 'Cooling exceeds heat generation. The rig will hold at this setting.',
      teaching: false,
    };
  }

  const need = thoughtsToFirstGM(state, mods);
  if (need.gt(0)) {
    return {
      label: 'Running',
      body: `Meltdown in ${Math.ceil(eta)}s. This run is not yet worth an idea.`,
      teaching: false,
    };
  }

  return {
    label: 'Running',
    body: `Meltdown in ${Math.ceil(eta)}s. The idea is growing — hold as long as you dare.`,
    teaching: false,
  };
}
